import React, { useEffect, useState } from "react";
import { getApiBaseUrl } from "../utils/baseUrl";

const labels = { FORMATION: "Formation", BRIGADE: "Brigade", UNIT: "Unit", BRANCH: "Branch", SUB_BRANCH: "Sub-branch", APPOINTMENT: "Appointment" };

export default function RegistrationStructure({ service, onChange }) {
  const [appointments, setAppointments] = useState([]);
  const [selection, setSelection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSelection([]);
    setAppointments([]);
    onChange(null);
    fetch(`${getApiBaseUrl()}/api/users/registration-appointments`, {
      cache: "no-store", signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) throw new Error("Could not load the organization structure. Please retry.");
      const data = await response.json();
      if (!Array.isArray(data.appointments) || data.appointments.some((item) => !Array.isArray(item.path))) {
        throw new Error("Could not load the organization structure. Please retry.");
      }
      if (!controller.signal.aborted) {
        setAppointments(data.appointments.filter((item) => item.unit === service).map((item) => ({
          ...item, path: item.path.filter((node) => node.type !== "SERVICE"),
        })));
      }
    }).catch((err) => {
      if (!controller.signal.aborted) setError(err.message || "Could not load the organization structure. Please retry.");
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [service, attempt, onChange]);

  if (loading) return <p role="status">Loading organization structure...</p>;
  if (error) return <div role="alert">{error} <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry</button></div>;
  if (!appointments.length) return <p role="status">No appointments are available for {service}. Ask your administrator to add them in Structure Manager.</p>;

  const levels = [];
  let candidates = appointments;
  for (let depth = 0; candidates.length; depth += 1) {
    const options = [...new Map(candidates.filter((item) => item.path[depth]).map((item) => [item.path[depth].id, item.path[depth]])).values()];
    if (!options.length) break;
    levels.push(options.sort((a, b) => a.name.localeCompare(b.name)));
    if (!selection[depth]) break;
    candidates = candidates.filter((item) => item.path[depth]?.id === selection[depth]);
  }

  return <div className="p-6 bg-emerald-100 border-4 border-emerald-700 rounded-lg">
    {levels.map((options, depth) => {
      const types = [...new Set(options.map((item) => item.type))];
      const label = types.length === 1
        ? (service !== "KA" && types[0] === "BRIGADE" ? "Base" : labels[types[0]] || "Structure item")
        : "Structure item";
      return <div key={depth}>
        <label htmlFor={`registration-level-${depth}`} className="block mt-4 text-lg font-bold">Select {label}</label>
        <select id={`registration-level-${depth}`} value={selection[depth] || ""}
          className="w-full p-4 border-2 border-emerald-700 rounded"
          onChange={(event) => {
            const next = selection.slice(0, depth);
            if (event.target.value) next.push(event.target.value);
            setSelection(next);
            onChange(appointments.find((item) => item.path.length === next.length && item.path.every((node, index) => node.id === next[index])) || null);
          }}>
          <option value="">Choose {label}</option>
          {options.map((node) => <option key={node.id} value={node.id}>{node.name}</option>)}
        </select>
      </div>;
    })}
  </div>;
}
